import React from 'react';
import styled from 'styled-components';

interface CardProps {
  children: React.ReactNode;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const StyledCard = styled.div<{
  $padding: CardProps['padding'];
  $shadow: CardProps['shadow'];
  $hover: boolean;
  $clickable: boolean;
}>`
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: ${props => props.theme.radius};
  overflow: hidden;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  
  ${props => {
    const padding = props.$padding || 'md';
    const shadow = props.$shadow || 'sm';
    
    const paddingStyles = {
      none: 'padding: 0;',
      sm: 'padding: 0.75rem;',
      md: 'padding: 1rem;',
      lg: 'padding: 1.5rem;'
    };
    
    const shadowStyles = {
      none: 'box-shadow: none;',
      sm: `box-shadow: ${props.theme.shadowSm};`,
      md: `box-shadow: ${props.theme.shadowMd};`,
      lg: `box-shadow: ${props.theme.shadowLg};`
    };
    
    return `
      ${paddingStyles[padding]}
      ${shadowStyles[shadow]}
      
      ${props.$hover ? `
        transition: all 0.2s ease;
        &:hover {
          transform: translateY(-2px);
          box-shadow: ${props.theme.shadowLg};
        }
      ` : ''}
    `;
  }}
`;

const Card: React.FC<CardProps> = ({ 
  children, 
  padding = 'md', 
  shadow = 'sm', 
  hover = false,
  style,
  onClick,
  ...props 
}) => {
  return (
    <StyledCard
      $padding={padding}
      $shadow={shadow}
      $hover={hover}
      $clickable={!!onClick}
      style={style}
      onClick={onClick}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

// Sub-components
const CardHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.card};
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0;
  
  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

const CardContent = styled.div`
  padding: 1rem;
`;

const CardFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.card};
`;

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
export default Card; 